/**
 * AlvaARBackend — real monocular SLAM in the browser via AlvaAR (WASM).
 *
 * Written against AlvaAR's ACTUAL API (verified from source, not guessed):
 *
 *   AlvaAR.Initialize(width, height, fov=45) -> instance
 *   instance.findCameraPose(imageData) -> Float32Array(16) | null
 *   instance.findCameraPoseWithIMU(imageData, quat{w,x,y,z}, motionSamples[]) -> mat | null
 *       (motionSamples are DRAINED by the call — it pops them)
 *   instance.findPlane(iterations=250) -> Float32Array(16) | null
 *   instance.getFramePoints() -> [{x, y}]   // PIXEL coords at init resolution
 *   instance.reset()
 *
 * Pose convention, taken from AlvaAR's own three.js connector, not invented:
 *   m = Matrix4.fromArray(pose)         // column-major
 *   q = quaternionFromMatrix(m); q.x = -q.x
 *   position = (pose[12], -pose[13], -pose[14])
 * i.e. the SLAM frame is Y-down/Z-forward (OpenCV) and we flip to Y-up/Z-back
 * (WebGL). Getting this wrong is the classic "world is mirrored/upside-down" bug.
 *
 * ⚠️ LICENSE: AlvaAR is GPLv3 (derived from OV²SLAM / ORB-SLAM2). Fine for this
 * non-commercial artwork; it must NOT leak into the commercial Simulark stack.
 * It stays behind the SpatialBackend seam precisely so it can be replaced.
 *
 * ⚠️ VENDORING: not bundled. AlvaAR ships as ONE ~4.3MB ES module with the WASM
 * embedded as base64 (there is no separate .wasm file). Place it at:
 *     client/public/vendor/alva_ar.js
 * See client/public/vendor/README.md.
 */

import type { Anchor, CameraPose, Look, MapHealth, SpatialBackend } from './SpatialBackend.js';
import { DevicePose, type Quat } from './DevicePose.js';
import type { FrameProvider } from './FrameProvider.js';
import { telemetry as defaultTelemetry, type SlamTelemetry, type TrackingState } from './telemetry.js';
import {
  GroundPlaneScale,
  isHorizontalPlane,
  planeFromAlvaMatrix,
  type ScaleEstimate,
} from './GroundPlaneScale.js';
import {
  cameraDiagonalFovDeg,
  frameGeometry,
  frameHorizontalFovDeg,
  trackerFovDeg,
  viewGeometry,
  type ViewGeometry,
} from './cameraFov.js';
import { PoseAcceptance } from './poseGuard.js';
import { SceneDepth, type SceneDepthStatus } from './SceneDepth.js';
import { FeatureTracker, type TrackResult } from './FeatureTracker.js';
import { MapContinuity, type RawPoseVerdict } from './mapContinuity.js';

const VENDOR_PATH = '/vendor/alva_ar.js';
/**
 * Processing buffer sizing. The buffer MUST match the camera's aspect ratio.
 *
 * AlvaAR's getCameraIntrinsics ends with `const f = Math.min(fx, fy)` and assigns
 * that single value to BOTH fx and fy — it hard-assumes square pixels. Feeding it
 * an anisotropically scaled image silently breaks the pinhole model: every
 * reprojection is wrong by the stretch factor, ORB descriptors stop matching
 * across viewpoints (they are rotation-invariant, not anisotropic-scale
 * invariant), and triangulation between keyframes becomes geometrically
 * inconsistent — a sparse map whose new observations barely connect to old ones.
 *
 * That was happening here: `drawTo(ctx, 640, 480)` stretched a portrait 3:4 phone
 * frame into a 4:3 landscape buffer, a 1.78x horizontal stretch.
 *
 * So we pick the buffer from the source's real aspect at init time, capping the
 * long edge for latency. 640x480 and 480x640 are both ~307k pixels.
 */
const PROC_LONG_EDGE = 640;
const PROC_MAX_PIXELS = 640 * 480;

// Field of view now lives in spatial/cameraFov.ts, shared with the renderer.
// They used to be separate constants that disagreed by 32%, which unsticks the
// world regardless of how good tracking is. Read at Initialize() only — AlvaAR
// fixes its intrinsics there, so changing it needs a reset to take full effect.
const SAMPLE_MS = 60;
const DEG = Math.PI / 180;

/**
 * How far in front of the participant a fragment is placed.
 *
 * Expressed in METRES and converted to SLAM units via the metric scale estimate.
 * Monocular scale is arbitrary per session, so a fixed unit depth put fragments
 * at wildly different real distances run to run — 34% of one capture's entire
 * walked path, 0.8% of another's.
 */
const ANCHOR_DEPTH_M = 3.2;

/**
 * Fallback depth as a fraction of the room's own measured depth.
 *
 * The middle rung of the ladder in `anchorDepthUnits()`: when metres are unknown
 * but the scene has been triangulated, "somewhat nearer than the things in the
 * room" is a well-posed answer that carries no scale assumption at all. Below 1
 * so text sits in front of the furniture it is measured against rather than
 * inside it, and high enough that the participant must walk to reach it.
 */
const ANCHOR_DEPTH_SCENE_FRACTION = 0.8;

/**
 * Last-resort depth, in raw SLAM units, when the room has not been measured.
 *
 * ⚠️ This is a lottery ticket and is known to lose. A SLAM unit is arbitrary, so
 * this lands anywhere: in the capture of 2026-08-22 the session's scale was
 * ~0.2 m/unit, making the value of the time (2.5; it is 3.2 now) a placement
 * half a metre from the participant's face — crossed in half a second at
 * walking pace, which is why nothing appeared attached to the room. It survives
 * only because SOMETHING must be returned in the first seconds, before any
 * triangulation has succeeded.
 */
const ANCHOR_DEPTH_UNITS = 3.2;

/**
 * Sideways jitter of a default anchor, as a fraction of its placement distance.
 *
 * Was a fixed ±0.8 raw SLAM units, which is 4 cm in one session and a metre in
 * the next. 0.25 reproduces exactly that ±0.8 at the 3.2-unit default depth.
 * Applied once, at creation, from the depth the anchor was first given, and
 * then frozen — so a later depth correction still slides the text along its
 * line of sight without swinging it sideways.
 */
const ANCHOR_JITTER_FRACTION = 0.8 / 3.2;

/**
 * Angular spread applied to a fragment's default anchor direction, in degrees.
 *
 * ⚠️ Added 2026-08-26 to fix fragments arriving CRAMMED IN ONE PLACE. Before
 * this, `anchor()`'s default path pointed every new fragment straight along
 * `look()` — wherever the camera happened to be facing the instant that
 * fragment's anchor was first requested — offset only by a small (~0.8 unit)
 * WORLD-SPACE jitter, not an angular one. Both `Field.tsx`'s dwell loop and
 * `PointText.tsx`'s draw loop call `anchor()`/`resolve()` for every fragment
 * already in `live` on the very first render tick, and the corpus seeds many
 * fragments at once at session start — so they all anchored to the same
 * bearing the participant happened to be facing at t=0, a tight cluster
 * directly ahead rather than a field around them.
 *
 * A per-id hash gives each fragment its OWN yaw/pitch offset instead, so the
 * initial batch scatters across a hemisphere. Pitch is kept much narrower than
 * yaw — a fragment thrown 70° upward or downward reads as broken placement,
 * not as "somewhere in the room."
 */
const ANCHOR_SPREAD_YAW_DEG = 70;
const ANCHOR_SPREAD_PITCH_DEG = 16;

/**
 * Steps between findPlane() calls. It runs 250 RANSAC iterations, so it is far
 * too expensive for every frame — and pointless there anyway, since the estimate
 * is a median over time, not a per-frame quantity.
 */
const PLANE_EVERY = 25;

/**
 * Longest accepted floor hit, as a multiple of the camera's height above it.
 *
 * A ray nearly parallel to the floor intersects it a very long way off, which
 * would fling a fragment to the horizon. At eye height ~1.5 m, 12x is ~18 m —
 * beyond any indoor room, so anything further is a grazing ray, not a surface
 * the participant is looking at. Expressed in heights rather than metres so it
 * holds whatever arbitrary scale the map picked.
 */
const MAX_FLOOR_HIT_HEIGHTS = 12;

/** Lift text this far above the floor (metres) so it is not half-buried in it. */
const FLOOR_TEXT_LIFT_M = 0.35;

/**
 * How long to keep telling the participant to walk around before giving up.
 *
 * ⚠️ **This no longer gates anchoring.** It once did, twice, in both directions,
 * and both were wrong — see the StoredAnchor note. Anchor commitment is now
 * decided by WHICH RUNG of the placement ladder produced the depth, not by any
 * clock, because a clock cannot know whether the number it is about to freeze
 * is a measurement or a guess.
 *
 * All that survives here is `mapHealth()`'s hint: after this long, stop asking
 * for a floor that on most phones never arrives, and report the degraded truth
 * without nagging.
 */
const ANCHOR_GRACE_MS = 9000;

/**
 * How anchors handle an unproven map — provisional placement, then commitment.
 *
 * ## The dead end this replaces
 *
 * There used to be a boolean, `WITHHOLD_ANCHORS_UNTIL_HEALTHY`, and it was
 * flipped twice in one day because **both of its settings were wrong**:
 *
 *  - **Withholding ON** meant nothing appeared until the map found a floor AND
 *    locked a scale, or a 25 s deadline passed. On a phone the floor fit
 *    usually never arrives, so in practice every session waited the full 25 s
 *    staring at an empty room. Worse, the deadline was measured from first
 *    successful track, so a session that never tracked never released anchors
 *    at all.
 *  - **Withholding OFF** meant every fragment burned a permanent world
 *    coordinate the instant it appeared — including its DEPTH, guessed from
 *    whatever the scene estimator had at second zero, which is the least
 *    reliable moment of the session. Text ended up at the wrong distance and
 *    stayed there, which reads exactly like "not anchored".
 *
 * Both failures come from treating placement as a single irreversible decision
 * gated on a timer. It is not one decision — it is two quantities with very
 * different confidence profiles.
 *
 * ## What actually happens now
 *
 * **Direction is certain immediately; depth is not.** Where the participant is
 * looking is known the moment the fragment appears and never improves. How far
 * away to put it depends on scale and scene depth, which take seconds to
 * settle and often never fully lock.
 *
 * So an anchor freezes its origin and its world-space direction at first sight
 * — those never move again, which is what makes it a real world anchor that
 * parallaxes correctly — and leaves only the distance along that ray open. It
 * renders immediately at the best depth currently available. If that depth came
 * from a real measurement (metric scale, or triangulated scene depth) it commits
 * at once. If it came from the rung-3 lottery constant it stays open — for as
 * long as it takes — and slides to the true distance the moment a measurement
 * arrives.
 *
 * The visible result is text that appears at once and *settles* into the room,
 * rather than either appearing wrong forever or not appearing at all. The
 * correction slides the text along the line of sight, so it never jumps
 * sideways — the participant reads it as the writing finding its distance.
 *
 * An anchor as stored is therefore a ray frozen at first sight, plus a distance
 * along it that stays open until the anchor commits.
 *
 * ## Anchors placed before the first tracked pose (cold start)
 *
 * The corpus seeds anchor on the field's first render tick, which is usually
 * BEFORE AlvaAR has finished loading its 4.3 MB module and returned a pose. The
 * camera position is then the origin, which is right: AlvaAR's world origin IS
 * the camera at map init (every capture that starts at init shows
 * `pos = [0,0,0]` for its first tracked frames).
 *
 * Its ORIENTATION is not the camera's at init, though — measured, not assumed.
 * On the IMU path (`findCameraPoseWithIMU`, i.e. any phone with motion events)
 * every capture shows the first SLAM yaw/pitch equal to the IMU's to within
 * 0.4° (iPhone 184.0/−21.9 vs 183.6/−21.8; Oppo 240.5/−32.8 vs 240.4/−32.8),
 * and they track within 0.3-4.7° mean thereafter: AlvaAR's world is the
 * device-orientation frame. So an identity orientation — what an untracked
 * backend used to place with — pointed every cold-start fragment at yaw 180°,
 * pitch 0° of the IMU frame: about where an iPhone faces at page load (its
 * alpha starts near 0) but tilted up by however far the phone was pitched, and
 * on Android (compass-referenced) towards magnetic south, wherever the
 * participant was facing. Cold-start placement and projection therefore use
 * the IMU quaternion while nothing has been tracked on that path — see
 * `placementPose()`. Vision-only sessions (no motion events) keep identity,
 * which there IS the camera at map init.
 *
 * ⚠️ 2026-10-05: all of the above describes the IMU POSE path, which is now off
 * by default (USE_IMU_ORIENTATION_POSE). "AlvaAR's world is the device-orientation
 * frame" turned out to hold for its ORIENTATION only: the vendor copies the
 * input quaternion through and its translation is the vision map's, in a
 * different frame. On the default vision path a phone with motion events is a
 * vision-only session as far as this note is concerned: identity at map init.
 *
 * With REANCHOR_MAP_TO_DEVICE_ORIENTATION on (vision path, sensor present) the
 * vision map's frame IS made the device's at its first pose, so the cold start
 * again uses the device quaternion — see `placementPose()`.
 */
interface StoredAnchor {
  /** Camera position at first sight. Frozen — this is the ray's origin. */
  origin: Vec3;
  /** Unit direction in WORLD space, frozen at first sight. Never recomputed. */
  dir: Vec3;
  /** Distance along `dir`. The only quantity refined after first sight. */
  depth: number;
  /** Small fixed world offset so a cluster does not collapse onto one line. */
  offset: Vec3;
  /** True while `depth` may still be corrected. */
  provisional: boolean;
  /**
   * Which rung of the placement ladder produced the current `depth`.
   *
   * Kept so a committed anchor can still be UPGRADED when something strictly
   * better arrives — specifically rung 2 (scene depth, scale-free) to rung 1
   * (a locked metric scale). Fragments anchored early otherwise keep whatever
   * they were first given forever, so a scale that locks at 21 s never reaches
   * anything placed before it, which was the whole of STATUS §8.5.
   *
   * Upgrades only ever move up the ladder, and a floor hit (rung 0 here) is a
   * real surface at a real distance and is never revised.
   */
  rung: 0 | 1 | 2 | 3;
  /** Surface normal, when the placement came from a floor hit. */
  normal?: Vec3;
}

// Pose-jump vetoing lives in spatial/poseGuard.ts — extracted so the logic that
// decides whether to believe the tracker can be tested without WASM or a device.
// `PoseAcceptance` there owns WHEN the guard may judge (see its note: a veto
// used to disarm the guard for the very next frame).

/**
 * Consecutive failed `Initialize()` calls before the backend gives up for good.
 *
 * Each attempt instantiates a fresh ~4.3 MB WASM module. A failure here is
 * typically an out-of-memory on a low-tier phone, which retrying every 60 ms
 * only makes worse — it used to retry forever and never let anything fall back.
 */
const MAX_INIT_FAILURES = 2;

/**
 * Consecutive tracker exceptions before the instance is rebuilt (counted as a
 * reset). 10 steps ≈ 0.6 s.
 *
 * The heap-growth case is handled without a rebuild (`refreshHeapViews`); this
 * is the backstop for anything else that leaves every call throwing. It used to
 * be a bare `catch` that turned every exception into "lost", silently, forever.
 */
const MAX_CONSECUTIVE_STEP_ERRORS = 10;

/** Tracker-exception console messages are rate-limited to one per this long. */
const ERROR_LOG_EVERY_MS = 5000;

/**
 * Consecutive non-horizontal `findPlane` results before a known floor is
 * dropped. One wall, table or bad RANSAC fit used to wipe a good floor at once.
 */
const PLANE_REJECTS_TO_CLEAR = 3;

/** Relative aspect change of the source that forces a rebuild (rotation, renegotiation). */
const ASPECT_TOLERANCE = 0.03;

/**
 * Hard limit on IMU samples per findCameraPoseWithIMU call — DO NOT RAISE.
 *
 * AlvaAR allocates its IMU buffer as `new SharedMemory(module, HEAPF64, 256)`,
 * i.e. _malloc(256 * 8) = 2048 bytes. The payload it writes is
 * [qw,qx,qy,qz,count] followed by 7 doubles per sample, so the buffer holds
 * (256 - 5) / 7 = 35 samples.
 *
 * Nothing catches an overrun. SharedMemory builds its view with
 * `new Float64Array(buffer, ptr, numBytes)` — the third argument is an ELEMENT
 * count, not a byte count, so the view is 8x longer than the allocation and
 * heap.set() happily writes past the end into whatever malloc placed next.
 *
 * That is reachable in normal use: DevicePose accumulates motion while AlvaAR's
 * ~4.3MB WASM module loads, so the very first IMU-fused call can arrive with a
 * full buffer and scribble several KB over the SLAM system's own allocations,
 * right as the map is being initialised.
 *
 * We keep the NEWEST samples: they are the ones adjacent to the current frame.
 */
const MAX_IMU_SAMPLES = 35;

/**
 * Which AlvaAR call produces the pose: `findCameraPoseWithIMU` (true) or
 * `findCameraPose` (false). ⚠️ **Default flipped to false on 2026-10-05 — this
 * changes field-verified behaviour** (iPhone and Oppo sessions all ran the IMU
 * path). Flip back here if a phone session says so.
 *
 * Evidence (ADVIO-15 offline replay, real backend + real vendored WASM, 10 seeds;
 * nothing measured on a phone):
 *  - The vendored C++ IGNORES the motion samples. Repo gyro mapping, permuted
 *    axes, zeroed gyro and all-zero IMU are bit-identical. The output rotation is
 *    exactly the input quaternion; the translation is the vision translation.
 *  - So on that path position lives in the vision map's frame and orientation in
 *    the IMU's. Everything that combines them — body-frame motion, `worldUp()`,
 *    the floor test, anchor rays, triangulation — mixes two frames. Short-window
 *    drift vs ARKit (RPE 1 s / 5 s) is 0.07 / 0.13 m on the vision path and
 *    0.28 / 1.08 m on the IMU path, from the same translations.
 *  - The field note "rotation is effectively exact because it is IMU-driven"
 *    (STATUS §5) compared the IMU's yaw with the gyro's — i.e. the sensor with
 *    itself; it is not evidence about the map.
 *
 * Rotation, measured against ground truth (relative rotation error per 0.2 s,
 * median; in the fastest-turning 10% of the walk in brackets): vision 0.25°
 * (0.54°); IMU path fed ARKit's fused quaternion 0.09° (0.13°); IMU path fed a
 * plain gyro+accel complementary filter 0.92° (1.13°). A phone's
 * `deviceorientation` lies somewhere between those two IMU inputs — unmeasured.
 * The vision path never lost track in the fast segments of that walk.
 *
 * Risk of false: the camera rotation is now vision's, not the IMU's. Under
 * rotation-dominant motion (STATUS §8.4 — not present in that dataset, whose
 * turns peak at ~38°/s) vision rotation can be worse, and when the visual
 * track drops the view freezes instead of turning with the phone (on the IMU
 * path rotation keeps following the IMU through a loss — see the no-track
 * handling in `step()`). Neither has been seen on a device.
 */
export const USE_IMU_ORIENTATION_POSE = false;

/**
 * Re-anchor every AlvaAR map's world frame to the device's gravity-aligned
 * frame ("option (c)", STATUS §8.20). Vision pose path only.
 *
 * ## The problem
 *
 * Every vision-path loss ends in a brand-new AlvaAR map (631/631 on 8 real
 * sequences), and a new monocular map's world frame is the camera at its own
 * first keyframe — an arbitrary rotation of the previous one. So the orientation
 * the renderer uses jumped by an arbitrary rotation at every restart: every
 * 2–5 s under sustained turning (TUM fr1_360: 34.5° of 1-s orientation error in
 * what was shown vs 5.3° while tracked).
 *
 * ## What this does
 *
 * At the FIRST pose of each map (the cold-start map included) one fixed rotation
 * is chosen,
 *
 *     A = q_dev(t₀) ⊗ q_map(t₀)⁻¹
 *
 * where q_map is the vendor pose's orientation in the app frame (camera body →
 * map world, `poseFromAlvaMatrix`) and q_dev is `DevicePose.quaternion()` for the
 * SAME frame (camera/device body → gravity-aligned Y-up world). Every quantity
 * of that map is then expressed in A's frame — orientation A⊗q, position A·p,
 * floor point and normal A·x — at the one place each enters the backend
 * (`alignToDevice`, `sampleGroundPlane`). Everything downstream (anchors,
 * `SceneDepth` and its cloud, `worldUp()`, floor raycasts, `look()`, telemetry)
 * only ever sees aligned values. A pure rotation about the map origin changes no
 * distance, no angle and no triangulation result, so per-map geometry is
 * untouched; only the frame each map is written in changes, and every map's
 * frame is now (nearly) the device's: up is +Y and the displayed orientation
 * stays continuous with the device across a restart.
 *
 * What it does NOT do: position continuity (a new map still has a new origin and
 * a new scale, so anchors are still dropped at a restart), and it never feeds
 * anything back into the vendor (the raw pose stream is unchanged).
 *
 * Off (identity, the old behaviour) when: this constant/the dep is false; the IMU
 * pose path is in use (its orientation is already the IMU's); or the device
 * reports no orientation sensor, or a non-finite quaternion, at the map's first
 * pose — that map then stays in the vendor's frame for its whole life (A is
 * decided once per map and never revised, because rotating a frame that anchors
 * and scene points were already written in would move them).
 *
 * ⚠️ Yaw: the device's tilt comes from the accelerometer and is trustworthy;
 * its yaw is not (iOS `deviceorientation` alpha has an arbitrary origin and
 * drifts; Android's absolute event follows a magnetometer). A absorbs any yaw
 * origin and any yaw drift accumulated BEFORE a map starts, but a map inherits
 * the device's yaw error at its first frame, and the error between the device's
 * yaw at successive restarts is what the participant sees at each restart.
 *
 * ## Why on (2026-10-05) — offline evidence only, NOT verified on a phone
 *
 * 8 real sequences (ADVIO 10/15/16/20, TUM fr1 rpy/360/xyz/desk), real backend +
 * real vendored WASM, 5 seeds; device orientation idealised (ARKit / ground
 * truth) and realistic (complementary filter on raw gyro+accel / ground truth
 * with 1.5° tilt noise, 37° yaw offset, 3°/min yaw drift):
 *  - the vendor's raw pose stream is bit-identical on/off (53,995/53,995
 *    frames); per-map ATE, restarts (250/250, 0 false), uptime, vetoes, RPE,
 *    SceneDepth, floor acceptance and scale states are unchanged;
 *  - 1-s windows of the shown orientation > 10° off: 9.9% → 7.0% pooled
 *    (fr1_360 72% → 44%, fr1_rpy 31% → 16%, advio-16 16% → 12%); error at a
 *    restart, median 48° → 8.4° (p95 145° → 37°); 7/250 restarts got worse,
 *    by ≤ 3.4°; same picture with the realistic source (7.2%, 9.1°);
 *  - a constant device yaw offset changes nothing (exactly); 10°/min of yaw
 *    drift or 100 ms of camera-vs-sensor latency still leave it far better than
 *    off (7.0% / 7.2% vs 9.9%).
 * Known limit: a map's first pose can be off its settled frame (median 3.7°,
 * p90 14°, up to ~70° on long outdoor maps whose frame rotates in the first
 * seconds) — that error shows up as the jump at the NEXT restart.
 * Flip to false to restore the vendor-frame behaviour exactly.
 */
export const REANCHOR_MAP_TO_DEVICE_ORIENTATION = true;

/** On-device calibration. AlvaAR's own connector implies these; verify per device. */
const NEGATE_QUAT_X = true;
const NEGATE_POS_Y = true;
const NEGATE_POS_Z = true;

type Vec3 = { x: number; y: number; z: number };

export interface AlvaInstance {
  findCameraPose(frame: ImageData): Float32Array | null;
  findCameraPoseWithIMU(
    frame: ImageData,
    orientation: { w: number; x: number; y: number; z: number },
    motion: { timestamp: number; gx: number; gy: number; gz: number; ax: number; ay: number; az: number }[],
  ): Float32Array | null;
  findPlane(iterations?: number): Float32Array | null;
  getFramePoints(): { x: number; y: number }[];
  reset(): void;
}
export interface AlvaModule {
  AlvaAR: { Initialize(w: number, h: number, fov?: number): Promise<AlvaInstance> };
}

/** Why the backend has permanently given up — see `AlvaARBackend.failed`. */
export interface AlvaFailure {
  /** 'load': the vendored module could not be imported. 'init': Initialize() kept failing. */
  stage: 'load' | 'init';
  reason: string;
  /** Initialize() attempts made (0 for a load failure). */
  attempts: number;
}

/** The 2D surface frames are drawn into for AlvaAR. Injectable for tests. */
export interface ProcessingSurface {
  canvas: CanvasImageSource;
  ctx: CanvasRenderingContext2D;
}

/** Collaborators, injectable so the real backend logic can be tested without a browser or WASM. */
export interface AlvaARBackendDeps {
  loadModule: () => Promise<AlvaModule | null>;
  createSurface: (w: number, h: number) => ProcessingSurface | null;
  createTracker: () => { update(src: CanvasImageSource): TrackResult; reset(): void };
  pose: DevicePose;
  telemetry: SlamTelemetry;
  /** Wall clock for latency measurement only. Placement timing uses frame timestamps. */
  now: () => number;
  setInterval: (fn: () => void, ms: number) => number;
  clearInterval: (id: number) => void;
  /** Override `USE_IMU_ORIENTATION_POSE` for this instance (tests, offline harness). */
  useImuOrientationPose?: boolean;
  /** Override `REANCHOR_MAP_TO_DEVICE_ORIENTATION` for this instance (tests, offline harness). */
  reanchorMapToDevice?: boolean;
}

function defaultDeps(): AlvaARBackendDeps {
  return {
    loadModule: loadAlva,
    createSurface: (w, h) => {
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      return ctx ? { canvas, ctx } : null;
    },
    createTracker: () => new FeatureTracker(),
    pose: new DevicePose(),
    telemetry: defaultTelemetry,
    now: () => performance.now(),
    setInterval: (fn, ms) => window.setInterval(fn, ms),
    clearInterval: (id) => window.clearInterval(id),
  };
}

/**
 * Release what an AlvaAR instance holds, as far as the vendored API allows.
 *
 * Every `Initialize()` instantiates a NEW WASM module (alva_ar.js:
 * `AlvaARWasm().then(...)` per call), so a discarded instance is a whole
 * heap — 256 MB reserved at start in the vendored build. There is no
 * `dispose()` on `AlvaAR`; what exists is `SharedMemory.dispose()` (frees each
 * buffer) and the embind `System` object's `delete()`. Both are called,
 * defensively, and the references dropped so the module can be collected. The
 * vendored build has no threads and registers no global listeners, so nothing
 * else keeps it alive.
 */
export function disposeAlvaInstance(inst: AlvaInstance | null | undefined): void {
  if (!inst) return;
  const any = inst as unknown as Record<string, unknown>;
  for (const k of ['memCam', 'memObj', 'memPts', 'memIMU', 'memImg']) {
    try {
      (any[k] as { dispose?: () => void } | undefined)?.dispose?.();
    } catch {
      /* already freed, or the module aborted */
    }
  }
  try {
    (any.system as { delete?: () => void } | undefined)?.delete?.();
  } catch {
    /* already deleted */
  }
  try {
    any.system = null;
    any.wasm = null;
  } catch {
    /* frozen object: nothing more to drop */
  }
}

const HEAP_NAMES = ['HEAP8', 'HEAPU8', 'HEAP16', 'HEAPU16', 'HEAP32', 'HEAPU32', 'HEAPF32', 'HEAPF64'] as const;
/** Original view length of each SharedMemory, recorded while still attached. */
const viewLengths = new WeakMap<object, number>();

/**
 * Re-point an instance's typed-array views at the CURRENT WASM heap.
 *
 * ## The failure this prevents — reproduced, not hypothesised
 *
 * The vendored build allows memory growth. When the heap grows, the old
 * `ArrayBuffer` is detached — but each `SharedMemory` captured
 * `module.HEAPU8` (etc.) at construction and never refreshes it. From then on
 * EVERY call throws (`TypeError: Cannot perform %TypedArray%.prototype.set on a
 * detached ArrayBuffer`), and the old bare `catch` turned that into "lost" for
 * the rest of the session. A long walk grows the map, and the map grows the
 * heap.
 *
 * Verified 2026-10-04 by running the vendored alva_ar.js in Node over real
 * capture frames and forcing growth with `_malloc(400 MB)`: without this,
 * 0/10 frames tracked afterwards, all throwing; with it, 69/70 tracked and the
 * map survived — the one loss being the frame during which the heap grew (its
 * pose read-back hits the stale view inside the vendor method). Re-pointing is
 * done outside vendor code, on the instance's own fields, and is a no-op on an
 * instance that does not look like the vendored one.
 */
export function refreshHeapViews(inst: AlvaInstance): number {
  const any = inst as unknown as {
    wasm?: { module?: Record<string, unknown> } | null;
  } & Record<string, unknown>;
  const mod = any.wasm?.module;
  if (!mod) return 0;
  let refreshed = 0;
  for (const k of ['memCam', 'memObj', 'memPts', 'memIMU', 'memImg']) {
    const m = any[k] as
      | {
          type?: { buffer: ArrayBufferLike };
          heap?: { length: number } | null;
          ptr?: number | null;
          TypedArray?: unknown;
        }
      | undefined;
    if (!m || m.ptr == null || typeof m.TypedArray !== 'function' || !m.heap) continue;
    if (!viewLengths.has(m) && m.heap.length > 0) viewLengths.set(m, m.heap.length);
    const name = HEAP_NAMES.find(
      (n) => (mod[n] as { constructor?: unknown } | undefined)?.constructor === m.TypedArray,
    );
    if (!name) continue;
    const current = mod[name] as { buffer: ArrayBufferLike };
    if (m.type === current) continue;
    const len = viewLengths.get(m);
    if (len === undefined) continue;
    const Ctor = m.TypedArray as new (b: ArrayBufferLike, off: number, len: number) => { length: number };
    try {
      m.heap = new Ctor(current.buffer, m.ptr, len);
      m.type = current;
      refreshed++;
    } catch {
      /* does not fit: leave it, the step-error backstop will rebuild */
    }
  }
  return refreshed;
}

/**
 * A camera pose from AlvaAR's 16 floats, in the app's frame — plus whether it
 * is usable at all. Pure, so the conversion the field depends on is testable.
 */
export function poseFromAlvaMatrix(mat: ArrayLike<number>): {
  position: Vec3;
  orientation: Quat;
  finite: boolean;
} {
  let finite = mat.length >= 16;
  for (let i = 0; finite && i < 16; i++) if (!Number.isFinite(mat[i]!)) finite = false;
  const q = quatFromMat4(mat);
  if (NEGATE_QUAT_X) q.x = -q.x;
  const position: Vec3 = {
    x: mat[12]!,
    y: NEGATE_POS_Y ? -mat[13]! : mat[13]!,
    z: NEGATE_POS_Z ? -mat[14]! : mat[14]!,
  };
  if (![q.x, q.y, q.z, q.w, position.x, position.y, position.z].every(Number.isFinite)) finite = false;
  return { position, orientation: q, finite };
}

let cachedModule: AlvaModule | null | undefined;
/**
 * Why the LAST load attempt failed, if it did. The previous version of this
 * function swallowed the error completely — a failed import and a missing file
 * looked identical, so a real device capture could report backend:"vo" with
 * absolutely no way to tell why. Read via `alvaLoadError()`, shown in the HUD.
 */
let lastLoadError: string | null = null;

/**
 * Import the vendored module, working around Vite's dev server.
 *
 * Vite refuses to serve a /public file through its import transform — it answers
 * 500 with "This file is in /public ... should not be imported from source
 * code" — and appends `?import` to the request whether the specifier is relative
 * or fully qualified, because it is same-origin either way.
 *
 * The consequence was severe and silent: **AlvaAR never loaded under `npm run
 * dev` or `npm run phone`**, so every local test ran on the VO fallback with no
 * map at all, while production (static /public serving) worked fine. The error
 * was swallowed by a bare `catch`, so nothing anywhere said so.
 *
 * Direct import is tried first because in production it is free. If Vite eats it,
 * fetch the bytes and import a blob URL, which no bundler middleware can
 * intercept. AlvaAR is a single self-contained file with its WASM embedded as
 * base64, so it has no relative imports that a blob URL would break.
 */
/**
 * Dynamic import that Vite cannot see.
 *
 * `/* @vite-ignore *\/` is NOT sufficient: Vite still rewrites the call to
 * `__vite__injectQuery(url, 'import')`, appending `?import` to the request —
 * verified by reading the transformed module back from the dev server. That
 * query is what makes its dev server answer 500 for /public files, and it would
 * defeat a blob-URL workaround too.
 *
 * Constructing the import inside `new Function` puts it beyond static analysis,
 * so the specifier reaches the browser untouched. No CSP here forbids it.
 */
const rawDynamicImport = new Function('u', 'return import(u)') as (
  u: string,
) => Promise<unknown>;

async function importAlvaModule(): Promise<AlvaModule> {
  const url = new URL(VENDOR_PATH, window.location.origin).href;
  try {
    return (await rawDynamicImport(url)) as AlvaModule;
  } catch (direct) {
    // Last resort: fetch the bytes and import a blob, which no dev-server
    // middleware can intercept.
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status} fetching ${VENDOR_PATH} (after: ${String(direct)})`);
    const blobUrl = URL.createObjectURL(await res.blob());
    try {
      return (await rawDynamicImport(blobUrl)) as AlvaModule;
    } finally {
      URL.revokeObjectURL(blobUrl);
    }
  }
}

async function loadAlva(): Promise<AlvaModule | null> {
  if (cachedModule !== undefined) return cachedModule;
  try {
    const mod = await importAlvaModule();
    if (mod && typeof mod.AlvaAR?.Initialize === 'function') {
      cachedModule = mod;
      lastLoadError = null;
    } else {
      cachedModule = null;
      lastLoadError = 'module loaded but AlvaAR.Initialize is not a function — wrong file at ' + VENDOR_PATH;
    }
  } catch (err) {
    cachedModule = null;
    lastLoadError = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
    // eslint-disable-next-line no-console -- deliberate: this failure was previously invisible everywhere
    console.error('[AlvaAR] failed to load', VENDOR_PATH, err);
  }
  return cachedModule;
}

/** Whether AlvaAR is vendored and loadable — drives backend auto-selection. */
export async function alvaAvailable(): Promise<boolean> {
  return (await loadAlva()) !== null;
}

/** Reason the last `loadAlva()` attempt failed, or null if it succeeded / hasn't run. */
export function alvaLoadError(): string | null {
  return lastLoadError;
}

/** Quaternion from a column-major 4x4 rotation (standard Shepperd's method). */
function quatFromMat4(m: ArrayLike<number>): Quat {
  const m11 = m[0]!, m12 = m[4]!, m13 = m[8]!;
  const m21 = m[1]!, m22 = m[5]!, m23 = m[9]!;
  const m31 = m[2]!, m32 = m[6]!, m33 = m[10]!;
  const trace = m11 + m22 + m33;
  let x: number, y: number, z: number, w: number;
  if (trace > 0) {
    const s = 0.5 / Math.sqrt(trace + 1.0);
    w = 0.25 / s;
    x = (m32 - m23) * s;
    y = (m13 - m31) * s;
    z = (m21 - m12) * s;
  } else if (m11 > m22 && m11 > m33) {
    const s = 2.0 * Math.sqrt(1.0 + m11 - m22 - m33);
    w = (m32 - m23) / s;
    x = 0.25 * s;
    y = (m12 + m21) / s;
    z = (m13 + m31) / s;
  } else if (m22 > m33) {
    const s = 2.0 * Math.sqrt(1.0 + m22 - m11 - m33);
    w = (m13 - m31) / s;
    x = (m12 + m21) / s;
    y = 0.25 * s;
    z = (m23 + m32) / s;
  } else {
    const s = 2.0 * Math.sqrt(1.0 + m33 - m11 - m22);
    w = (m21 - m12) / s;
    x = (m13 + m31) / s;
    y = (m23 + m32) / s;
    z = 0.25 * s;
  }
  return { x, y, z, w };
}

function quatRotate(q: Quat, v: [number, number, number]): Vec3 {
  const [vx, vy, vz] = v;
  const tx = 2 * (q.y * vz - q.z * vy);
  const ty = 2 * (q.z * vx - q.x * vz);
  const tz = 2 * (q.x * vy - q.y * vx);
  return {
    x: vx + q.w * tx + (q.y * tz - q.z * ty),
    y: vy + q.w * ty + (q.z * tx - q.x * tz),
    z: vz + q.w * tz + (q.x * ty - q.y * tx),
  };
}

/** Hamilton product a ⊗ b (apply b, then a). */
function quatMul(a: Quat, b: Quat): Quat {
  return {
    x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
    y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
    z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
    w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
  };
}

/** A unit copy of q, or null if it is non-finite or degenerate. */
function unitQuat(q: Quat | null | undefined): Quat | null {
  if (!q) return null;
  const { x, y, z, w } = q;
  if (![x, y, z, w].every(Number.isFinite)) return null;
  const n = Math.hypot(x, y, z, w);
  if (!(n > 1e-6)) return null;
  return { x: x / n, y: y / n, z: z / n, w: w / n };
}

/**
 * The fixed rotation that re-expresses a map in the device's gravity-aligned
 * frame (see REANCHOR_MAP_TO_DEVICE_ORIENTATION): A = q_dev ⊗ q_map⁻¹, so that
 * A ⊗ q_map = q_dev for the frame both were measured at. Null when either input
 * is unusable — never a non-finite rotation.
 */
export function mapAlignmentFor(mapOrientation: Quat, deviceOrientation: Quat | null | undefined): Quat | null {
  const qm = unitQuat(mapOrientation);
  const qd = unitQuat(deviceOrientation);
  if (!qm || !qd) return null;
  return unitQuat(quatMul(qd, { x: -qm.x, y: -qm.y, z: -qm.z, w: qm.w }));
}

/** A camera pose re-expressed through a map alignment A: orientation A⊗q, position A·p. */
export function alignPose(
  A: Quat,
  pose: { position: Vec3; orientation: Quat },
): { position: Vec3; orientation: Quat } {
  return {
    position: quatRotate(A, [pose.position.x, pose.position.y, pose.position.z]),
    orientation: quatMul(A, pose.orientation),
  };
}

export class AlvaARBackend implements SpatialBackend {
  readonly frame = 'world' as const;
  readonly persistent = true;

  private readonly deps: AlvaARBackendDeps;
  /** Kept under this name: runtime.devicePose and the recorder read `pose`. */
  private pose: DevicePose;
  private telemetry: SlamTelemetry;
  private alva: AlvaInstance | null = null;
  private canvas: CanvasImageSource | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private source: FrameProvider | null = null;
  /** Kept under this name: ReplayApp's stopInternalTimer clears `timer`. */
  private timer: number | null = null;

  private position: Vec3 = { x: 0, y: 0, z: 0 };
  private orientation: Quat = { x: 0, y: 0, z: 0, w: 1 };
  private anchors = new Map<string, StoredAnchor>();
  private points: { x: number; y: number }[] = [];
  private state: TrackingState = 'idle';
  private useIMU = false;

  /** Set once the frame source's aspect is known — see PROC_LONG_EDGE. */
  private procW = 0;
  private procH = 0;
  private module: AlvaModule | null = null;
  private initPending = false;
  /**
   * Incremented whenever the current instance stops being wanted — stop(), a
   * FOV/aspect rebuild, an error rebuild. An `Initialize()` that resolves under
   * an older generation is disposed instead of installed: one resolving after
   * stop() used to resurrect a stopped backend, and one resolving after a FOV
   * change re-installed the OLD FOV.
   */
  private gen = 0;
  private stopped = false;
  private initFailures = 0;
  private failure: AlvaFailure | null = null;
  private initWaiters: ((ok: boolean) => void)[] = [];
  private failureListeners = new Set<(f: AlvaFailure) => void>();
  /** Total tracker exceptions this session, and the current unbroken run. */
  private stepErrors = 0;
  private consecutiveStepErrors = 0;
  private lastErrorLogAt = -Infinity;
  private suppressedErrorLogs = 0;
  /**
   * Which map the current coordinates belong to. Incremented by every reset or
   * rebuild; stamped on `cameraPose()` so a pose captured before a reset can be
   * recognised and refused (`anchorAtImagePoint`).
   */
  private epoch = 0;
  private scale = new GroundPlaneScale();
  /**
   * How far away the room is, in SLAM units. Needs no scale, no floor and no
   * server, so it is the one depth signal that is almost always available — see
   * SceneDepth for why placement now leans on it rather than on metres.
   */
  private sceneDepth = new SceneDepth();
  /** Feature tracker for triangulation correspondence, not for pose. */
  private scaleTracker: { update(src: CanvasImageSource): TrackResult; reset(): void } | null = null;
  /** True while not tracking, after the triangulation tracker was reset for it. */
  private triangulationPaused = false;
  private stepCount = 0;
  /**
   * Last trustworthy floor: a point on it, the IMU-derived up, and the camera's
   * height above it. `up` is used for raycasting rather than the plane's own
   * normal; the normal (see `planeFromAlvaMatrix`) only decides floor vs wall.
   */
  private groundPlane: { point: Vec3; up: Vec3; height: number } | null = null;
  /** Consecutive non-horizontal plane results — see PLANE_REJECTS_TO_CLEAR. */
  private planeRejectRun = 0;
  /** `fov` argument AlvaAR was initialised with (see cameraFov.trackerFovDeg). */
  private trackerFovArg: number | null = null;
  /** Calibrated diagonal at Initialize() — a change needs a rebuild to apply. */
  private initDiagonal: number | null = null;
  /** Diagonal an Initialize() still in flight was started with. */
  private pendingDiagonal: number | null = null;
  /** Vetoes physically impossible pose jumps and owns when it may — see poseGuard.ts. */
  private gate = new PoseAcceptance();
  /**
   * When the backend started, on the FRAME clock (`FrameProvider.timestamp()`).
   * `mapHealth()`'s walking-hint deadline is measured from here.
   *
   * ⚠️ Measured from start(), never from first successful track: a session
   * that never tracked used to have its deadline never arrive. (It once gated
   * anchors too; it no longer does — see ANCHOR_GRACE_MS.)
   *
   * Frame clock rather than `performance.now()` so a replay — which steps
   * frames as fast or as slowly as the desk allows — reaches the deadline at
   * the same FRAME as the live run did. Live, the frame clock is
   * `performance.now()`, so nothing changes there.
   */
  private startedAt: number | null = null;
  /** Whether this instance may use `findCameraPoseWithIMU` — see USE_IMU_ORIENTATION_POSE. */
  private readonly imuPoseAllowed: boolean;
  /** Reads "no visual track" and "new map" out of AlvaAR's raw poses — see mapContinuity.ts. */
  private continuity = new MapContinuity();
  /** AlvaAR-internal map restarts detected this session (each also bumps the epoch). */
  private restarts = 0;
  /**
   * Vetoes counted by guards that a reset has since discarded. `gate.reset()`
   * zeroes its guard's tally, so without this `rejectedJumps()` — documented as
   * "this session" — silently dropped every veto from before each reset, and map
   * restarts made that happen about once a run (found by the 2026-10-05 replay:
   * 6 vetoes reported as 0).
   */
  private rejectedInEarlierMaps = 0;
  /** Whether maps may be re-anchored to the device frame — see REANCHOR_MAP_TO_DEVICE_ORIENTATION. */
  private readonly reanchorAllowed: boolean;
  /**
   * The current map's alignment A (vendor map frame → device gravity-aligned
   * frame), or null for identity. Decided once, at the map's first pose
   * (`mapAlignDecided`), and cleared with everything else in the map.
   */
  private mapAlign: Quat | null = null;
  private mapAlignDecided = false;

  constructor(deps: Partial<AlvaARBackendDeps> = {}) {
    this.deps = { ...defaultDeps(), ...deps };
    this.pose = this.deps.pose;
    this.telemetry = this.deps.telemetry;
    this.imuPoseAllowed = this.deps.useImuOrientationPose ?? USE_IMU_ORIENTATION_POSE;
    this.reanchorAllowed = this.deps.reanchorMapToDevice ?? REANCHOR_MAP_TO_DEVICE_ORIENTATION;
  }

  /**
   * The current map's device alignment A (a copy), or null when the map is in
   * the vendor's own frame — feature off, IMU path, no sensor at its first pose,
   * or no pose yet. Diagnostic (HUD, offline harness).
   */
  mapAlignment(): Quat | null {
    return this.mapAlign ? { ...this.mapAlign } : null;
  }

  /**
   * The device orientation for the frame being processed, validated: a unit
   * quaternion, or null without an orientation sensor or with a non-finite
   * reading. Read once per step, straight after the frame is grabbed — the
   * pose AlvaAR returns belongs to that frame. (JavaScript runs `step()` to
   * completion, so no orientation event can land between the grab and the
   * tracker call; what remains is the camera pipeline's own latency, which is
   * not visible from here — see HISTORY 2026-10-05, option (c).)
   */
  private deviceQuatForFrame(): Quat | null {
    if (!this.pose.hasSensor()) return null;
    return unitQuat(this.pose.quaternion());
  }

  /**
   * Whether maps are (or, before the first pose, will be) re-anchored to the
   * device frame. Mirrors the decision in `alignToDevice`; used by the cold start.
   */
  private deviceAlignedMaps(): boolean {
    return this.reanchorAllowed && !this.imuPosePath();
  }

  /**
   * THE choke point where a vendor pose enters the map — see
   * REANCHOR_MAP_TO_DEVICE_ORIENTATION. On the first finite pose of a map it
   * decides A from this pose and the device orientation sampled for the same
   * frame; every pose of the map then goes through the same A. Identity (the
   * candidate returned untouched, bit for bit) when the feature is off, on the
   * IMU path, without a sensor, or with an unusable device quaternion.
   */
  private alignToDevice(
    cand: { position: Vec3; orientation: Quat; finite: boolean },
    deviceQ: Quat | null,
  ): { position: Vec3; orientation: Quat; finite: boolean } {
    if (!cand.finite) return cand; // never decides A, never enters the map
    if (!this.mapAlignDecided) {
      this.mapAlignDecided = true;
      this.mapAlign =
        this.reanchorAllowed && !this.useIMU ? mapAlignmentFor(cand.orientation, deviceQ) : null;
    }
    // The IMU path's orientation is the IMU's own: never re-anchored (a session
    // that switches to it mid-map stops being aligned from that frame on).
    if (!this.mapAlign || this.useIMU) return cand;
    const p = alignPose(this.mapAlign, cand);
    return { position: p.position, orientation: p.orientation, finite: cand.finite };
  }

  /** A map-frame point or direction from the vendor, in the current map's aligned frame. */
  private toAlignedFrame(v: Vec3): Vec3 {
    return this.mapAlign ? quatRotate(this.mapAlign, [v.x, v.y, v.z]) : v;
  }

  /**
   * Whether poses come (or, before the first step, will come) from
   * `findCameraPoseWithIMU`, whose orientation is the IMU's and whose position
   * is the vision map's. Everything frame-sensitive keys on this.
   */
  private imuPosePath(): boolean {
    return this.useIMU || (this.imuPoseAllowed && this.pose.hasSensor() && this.pose.hasMotion());
  }

  /** AlvaAR map restarts detected (and handled as resets) this session. */
  mapRestarts(): number {
    return this.restarts;
  }

  setFrameSource(source: FrameProvider | null): void {
    this.source = source;
  }

  /** The frame clock: the current frame's timestamp, or the wall clock without a source. */
  private frameNow(): number {
    return this.source ? this.source.timestamp() : this.deps.now();
  }

  async start(): Promise<void> {
    this.stopped = false;
    this.startedAt = this.source ? this.source.timestamp() : null;
    await this.pose.start();
    this.telemetry.setBackend('alva');
    const mod = await this.deps.loadModule();
    if (this.stopped) return; // stopped while loading: start nothing
    if (!mod) {
      this.fail({ stage: 'load', reason: alvaLoadError() ?? 'AlvaAR module unavailable', attempts: 0 });
      return;
    }
    // AlvaAR's image size is fixed at Initialize() and must match the camera's
    // aspect, which we only learn once the source has a frame. So defer the
    // actual Initialize to the first step() that has a size.
    this.module = mod;
    this.state = 'initializing';
    this.timer = this.deps.setInterval(() => this.step(), SAMPLE_MS);
  }

  /**
   * Why the backend has permanently given up, or null while it is still in the
   * running.
   *
   * ⚠️ This is the signal a caller must act on: when non-null, `trackingState()`
   * is 'unavailable' for good, no further WASM instance will be created, and
   * nothing will be tracked until a NEW backend is started (the runtime should
   * swap to VOBackend). `onFailure()` delivers the same thing as an event.
   */
  get failed(): AlvaFailure | null {
    return this.failure;
  }

  /** Subscribe to permanent failure. Fires at most once. Returns an unsubscribe. */
  onFailure(cb: (f: AlvaFailure) => void): () => void {
    if (this.failure) {
      cb(this.failure);
      return () => {};
    }
    this.failureListeners.add(cb);
    return () => this.failureListeners.delete(cb);
  }

  private fail(f: AlvaFailure): void {
    if (this.failure) return;
    this.failure = f;
    this.state = 'unavailable';
    if (this.timer !== null) this.deps.clearInterval(this.timer);
    this.timer = null;
    // eslint-disable-next-line no-console -- a dead tracker must never again be invisible
    console.error(`[AlvaAR] giving up (${f.stage}): ${f.reason}`);
    this.settleWaiters(false);
    for (const cb of this.failureListeners) {
      try {
        cb(f);
      } catch {
        /* a bad listener must not break shutdown */
      }
    }
    this.failureListeners.clear();
  }

  private settleWaiters(ok: boolean): void {
    const waiters = this.initWaiters;
    this.initWaiters = [];
    for (const w of waiters) w(ok);
  }

  /**
   * Evidence that AlvaAR actually came up, for the runtime's backend chain.
   *
   * Resolves `true` once an instance is installed and `false` if Initialize()
   * has permanently failed (MAX_INIT_FAILURES). While no frame source has a size
   * yet there is nothing to wait for — Initialize() cannot even begin — so it
   * resolves `true` at once rather than holding the field closed; and on
   * timeout it resolves `true` too, because "not proven yet" is not "failed".
   * A failure after this has resolved is reported by `failed`/`onFailure`.
   */
  async confirmStarted(timeoutMs: number): Promise<boolean> {
    if (this.failure) return false;
    if (this.alva) return true;
    const size = this.source?.size();
    if (!this.initPending && !(size && size.width > 0 && size.height > 0)) return true;
    return new Promise<boolean>((resolve) => {
      let done = false;
      const finish = (ok: boolean) => {
        if (done) return;
        done = true;
        resolve(ok);
      };
      this.initWaiters.push(finish);
      setTimeout(() => finish(true), timeoutMs);
    });
  }

  /** Processing-buffer size for a source size: aspect kept, long edge and pixels capped. */
  private static procSize(size: { width: number; height: number }): { w: number; h: number } {
    const aspect = size.width / size.height;
    let w: number;
    let h: number;
    if (aspect >= 1) {
      w = Math.min(PROC_LONG_EDGE, size.width);
      h = Math.round(w / aspect);
    } else {
      h = Math.min(PROC_LONG_EDGE, size.height);
      w = Math.round(h * aspect);
    }
    const scale = Math.sqrt(PROC_MAX_PIXELS / (w * h));
    if (scale < 1) {
      w = Math.round(w * scale);
      h = Math.round(h * scale);
    }
    // Even dimensions: image pyramids halve repeatedly.
    w -= w % 2;
    h -= h % 2;
    return { w, h };
  }

  /**
   * Initialize AlvaAR at a buffer matching the source aspect. Runs on the first
   * step() where the frame source reports a size, and again after a rebuild.
   */
  private ensureAlva(): void {
    if (this.alva || this.initPending || this.failure || this.stopped || !this.module || !this.source) return;
    const size = this.source.size();
    if (!size || size.width <= 0 || size.height <= 0) return;
    const { w, h } = AlvaARBackend.procSize(size);

    this.initPending = true;
    const mod = this.module;
    const gen = this.gen;
    // The `fov` argument that makes AlvaAR's focal length the TRUE one for this
    // buffer — horizontal for portrait, horizontal/aspect for landscape. See
    // cameraFov.trackerFovDeg for the vendor arithmetic behind that.
    const fov = trackerFovDeg(w, h);
    const diagonal = cameraDiagonalFovDeg();
    this.pendingDiagonal = diagonal;
    void (async () => {
      let inst: AlvaInstance | null = null;
      try {
        inst = await mod.AlvaAR.Initialize(w, h, fov);
        if (gen !== this.gen) {
          // Stopped or rebuilt while this was loading: never install it.
          disposeAlvaInstance(inst);
          return;
        }
        const surface = this.deps.createSurface(w, h);
        if (!surface) throw new Error('2D canvas context unavailable');
        refreshHeapViews(inst); // records the original view lengths while attached
        this.alva = inst;
        this.trackerFovArg = fov;
        this.initDiagonal = diagonal;
        this.procW = w;
        this.procH = h;
        this.canvas = surface.canvas;
        this.ctx = surface.ctx;
        this.initFailures = 0;
        this.settleWaiters(true);
      } catch (err) {
        if (inst && this.alva !== inst) disposeAlvaInstance(inst);
        if (gen !== this.gen) return;
        this.initFailures++;
        const reason = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
        // eslint-disable-next-line no-console -- this used to fail silently and retry forever
        console.error(`[AlvaAR] Initialize failed (attempt ${this.initFailures}/${MAX_INIT_FAILURES})`, err);
        if (this.initFailures >= MAX_INIT_FAILURES) {
          this.fail({ stage: 'init', reason, attempts: this.initFailures });
        }
      } finally {
        if (gen === this.gen) {
          this.initPending = false;
          this.pendingDiagonal = null;
        }
      }
    })();
  }

  /** Drop the current instance and everything bound to its buffer size. */
  private releaseInstance(): void {
    this.gen++;
    disposeAlvaInstance(this.alva);
    this.alva = null;
    this.canvas = null;
    this.ctx = null;
    this.procW = 0;
    this.procH = 0;
    this.trackerFovArg = null;
    this.initDiagonal = null;
    this.initPending = false;
    this.pendingDiagonal = null;
  }

  /**
   * Forget everything derived from the current map. A reset picks a fresh
   * arbitrary origin, orientation AND scale, so nothing measured in the old one
   * means anything in the new one.
   */
  private clearMapState(): void {
    this.scale.reset();
    this.groundPlane = null;
    this.planeRejectRun = 0;
    this.anchors.clear();
    this.points = [];
    this.rejectedInEarlierMaps += this.gate.guard.totalRejected;
    this.gate.reset();
    // Scene depth is measured in the OLD map's units, which the new map does not
    // share — keeping it would place every fragment against a defunct yardstick.
    this.sceneDepth.reset();
    this.scaleTracker?.reset();
    this.triangulationPaused = false;
    // ⚠️ The pose too. It used to survive a reset at the OLD map's last value,
    // so anchors created between the reset and the new map's first pose mixed
    // an old-map origin with a new-map world — and a Watcher reply stamped
    // before the reset did the same. The new map's origin is the camera at its
    // init, which is exactly what the origin/identity (or, on the IMU path, the
    // IMU orientation — see placementPose) stands for.
    this.position = { x: 0, y: 0, z: 0 };
    this.orientation = { x: 0, y: 0, z: 0, w: 1 };
    // The next map gets its own alignment, decided at its own first pose.
    this.mapAlign = null;
    this.mapAlignDecided = false;
    this.epoch++;
  }

  /**
   * Throw the instance away and start a new map (aspect change, FOV change,
   * persistent tracker errors). Counted as a reset in telemetry.
   */
  private rebuild(): void {
    this.releaseInstance();
    this.clearMapState();
    this.continuity.reset();
    this.state = 'reset';
    this.consecutiveStepErrors = 0;
    this.telemetry.markReset();
  }

  /**
   * AlvaAR started a new map on its own (see mapContinuity.ts). The same as
   * `resetTracking()` minus the vendor reset — the vendor has already reset,
   * and calling `reset()` now would throw away the new map it just built:
   * everything measured in the old map's units goes (anchors, scene depth and
   * its cloud, floor, scale observations, guard history), the epoch is bumped
   * so poses stamped in the old map are refused, and telemetry counts it and
   * excludes the join from path length and stepRatio.
   *
   * The continuity reader is NOT reset: the pose that revealed the restart has
   * already been read and is the new map's first.
   */
  private onMapRestart(): void {
    this.clearMapState();
    this.restarts++;
    this.telemetry.markReset('restart');
  }

  stop(): void {
    this.stopped = true;
    this.pose.stop();
    if (this.timer !== null) this.deps.clearInterval(this.timer);
    this.timer = null;
    // Invalidate any Initialize() still in flight, and release the instance —
    // stop() used to drop the reference without freeing anything, and leave a
    // pending init free to install itself afterwards.
    this.releaseInstance();
    this.clearMapState();
    this.continuity.reset();
    this.scaleTracker = null;
    this.settleWaiters(false);
    if (!this.failure) this.state = 'idle';
  }

  /** Count, surface (rate-limited) and remember a tracker exception. */
  private noteStepError(err: unknown, where: string): void {
    this.stepErrors++;
    this.consecutiveStepErrors++;
    const now = this.deps.now();
    if (now - this.lastErrorLogAt >= ERROR_LOG_EVERY_MS) {
      const extra = this.suppressedErrorLogs ? ` (+${this.suppressedErrorLogs} suppressed)` : '';
      // eslint-disable-next-line no-console -- see MAX_CONSECUTIVE_STEP_ERRORS
      console.warn(`[AlvaAR] ${where} threw (${this.stepErrors} total)${extra}`, err);
      this.lastErrorLogAt = now;
      this.suppressedErrorLogs = 0;
    } else {
      this.suppressedErrorLogs++;
    }
  }

  /** Tracker exceptions this session. */
  trackerErrorCount(): number {
    return this.stepErrors;
  }

  /** The source's aspect no longer matches the processing buffer (rotation, renegotiation). */
  private aspectChanged(): boolean {
    if (!this.source || !this.procW || !this.procH) return false;
    const size = this.source.size();
    if (!size || size.width <= 0 || size.height <= 0) return false;
    const want = size.width / size.height;
    const have = this.procW / this.procH;
    return Math.abs(want - have) / have > ASPECT_TOLERANCE;
  }

  /** One tracking iteration. Also the unit the replay harness drives. */
  step(): void {
    if (this.failure || !this.source) return;
    if (this.startedAt === null) this.startedAt = this.source.timestamp();

    // The buffer is fixed at Initialize(). A source whose aspect has changed
    // (rotation, a renegotiated stream swapping width/height) would be drawn
    // into it anisotropically — the exact stretch PROC_LONG_EDGE's note warns
    // about — so rebuild at the new aspect instead, like a FOV change.
    if (this.alva && this.aspectChanged()) {
      this.rebuild();
      return;
    }

    this.ensureAlva();
    if (!this.alva || !this.ctx) return;

    const tGrab = this.deps.now();
    if (!this.source.drawTo(this.ctx, this.procW, this.procH)) return;
    // ⚠️ A fresh ~1.2 MB ImageData every step. Canvas 2D has no API that reads
    // pixels into an existing buffer (`getImageData` always allocates), and the
    // alternatives (WebCodecs `VideoFrame.copyTo`) change the pixel path, which
    // would break replay/live equivalence. Left as is, deliberately.
    const imageData = this.ctx.getImageData(0, 0, this.procW, this.procH);
    const grabMs = this.deps.now() - tGrab;
    // The device orientation that belongs to THIS frame, for a map's alignment
    // (REANCHOR_MAP_TO_DEVICE_ORIENTATION). Sampled here, at the grab, never
    // later — a stale quaternion at a map's first pose would be a constant
    // orientation error for that map's whole life.
    const frameDeviceQ = this.deviceAlignedMaps() ? this.deviceQuatForFrame() : null;

    const tTrack = this.deps.now();
    let mat: Float32Array | null = null;
    let errored = false;
    try {
      // Heap growth detaches the vendor's views; re-point them first. Cheap:
      // five identity comparisons when nothing has grown.
      refreshHeapViews(this.alva);
      const drained = this.pose.drainMotion();
      // Keep the newest MAX_IMU_SAMPLES — see the constant for why this is a
      // memory-safety bound, not a tuning knob.
      const motion =
        drained.length > MAX_IMU_SAMPLES ? drained.slice(-MAX_IMU_SAMPLES) : drained;
      // With USE_IMU_ORIENTATION_POSE off the samples are still drained (so
      // nothing accumulates) and simply not used. Once on the IMU path, stay on
      // it — even for a frame that brought no samples (the vendor ignores them
      // anyway) — rather than let one frame's rotation come from vision and the
      // next from the IMU: they are different frames.
      if (this.imuPoseAllowed && (motion.length > 0 || this.useIMU)) {
        this.useIMU = true;
        const q = this.pose.quaternion();
        mat = this.alva.findCameraPoseWithIMU(
          imageData,
          { w: q.w, x: q.x, y: q.y, z: q.z },
          motion,
        );
      } else {
        mat = this.alva.findCameraPose(imageData);
      }
      // The call above may itself have grown the heap.
      refreshHeapViews(this.alva);
      this.points = this.alva.getFramePoints() ?? [];
      this.consecutiveStepErrors = 0;
    } catch (err) {
      mat = null;
      errored = true;
      this.noteStepError(err, 'findCameraPose');
    }
    const trackMs = this.deps.now() - tTrack;

    if (this.consecutiveStepErrors >= MAX_CONSECUTIVE_STEP_ERRORS) {
      // Every call is failing and re-pointing the heap did not help: the
      // instance is unusable. Start a new map rather than stay 'lost' forever.
      this.rebuild();
      return;
    }

    // What AlvaAR said this step, after reading its raw stream for the two
    // things its API never says (mapContinuity.ts): no visual track, new map.
    const cand = !errored && mat && mat.length >= 16 ? poseFromAlvaMatrix(mat) : null;
    let signal: RawPoseVerdict | null = null;
    if (errored) this.continuity.skip();
    else if (!cand) this.continuity.noResult();
    else if (!cand.finite) this.continuity.skip();
    else signal = this.continuity.classify(mat!);

    if (errored) {
      // An exception is not evidence about the map either way: leave the
      // guard's settledness alone (see PoseAcceptance.judge's 'invalid').
      this.state = this.gate.everTracked ? 'lost' : 'initializing';
    } else if (cand && signal === 'no-track') {
      // A pose with no visual track behind it: exactly-zero translation before
      // the map exists, or the previous translation repeated bit for bit after
      // a loss. The IMU path returns one on every frame it cannot see, and they
      // used to be reported as 'tracking' — 17 pre-init frames at t=(0,0,0) on
      // every run, 769/777 frames on shuffled input. Genuinely lost: the next
      // real pose is judged as a relocalisation (or recognised as a new map).
      this.gate.noPose();
      this.state = this.gate.everTracked ? 'lost' : 'initializing';
      // On the IMU path the rotation IS a fresh measurement in the frame the
      // orientation already lives in (the IMU's), so keep following it — that
      // is what the view did through a loss before, now without the claim of
      // tracking. Position stays held. Never on the vision path, where a
      // repeated pose carries nothing new.
      if (this.useIMU && this.gate.everTracked) this.orientation = cand.orientation;
    } else if (cand) {
      if (signal === 'new-map' && this.gate.everTracked) {
        // AlvaAR threw its map away and started another: new origin, new
        // orientation, new arbitrary scale (3-7x different on ADVIO-15).
        // Taken as a relocalisation in the OLD map — all noPose() could do —
        // it kept every anchor, scene-depth point and floor observation
        // measured against a yardstick that no longer exists. Handle it as the
        // reset it is; this pose is then the new map's first, and the cleared
        // guard accepts it unconditionally.
        this.onMapRestart();
      }
      // Into the map's device-aligned frame (identity when the feature is off).
      // Decided on the map's first pose, which the cleared guard always accepts.
      const pose = this.alignToDevice(cand, frameDeviceQ);
      const d = Math.hypot(
        pose.position.x - this.position.x,
        pose.position.y - this.position.y,
        pose.position.z - this.position.z,
      );
      const verdict = this.gate.judge(d, pose.finite);
      if (verdict === 'accept') {
        this.orientation = pose.orientation;
        this.position = pose.position;
        this.state = 'tracking';
      } else if (verdict === 'veto') {
        // Physically impossible. Hold the last good pose and report lost, so the
        // floor estimator and anchors are not poisoned. The guard STAYS armed:
        // the next pose is judged against the last accepted one, not waved in.
        this.state = 'lost';
      } else {
        // Non-finite pose: never let NaN become the position anchors freeze.
        this.state = this.gate.everTracked ? 'lost' : 'initializing';
      }
    } else {
      // AlvaAR returns null for both "initializing" and "lost"; once we have
      // tracked at least once, null means we lost it — genuinely, so whatever
      // comes back next is a relocalisation (or, as it turns out, nearly always
      // a NEW map — see mapContinuity.ts) and is accepted as such.
      this.gate.noPose();
      this.state = this.gate.everTracked ? 'lost' : 'initializing';
    }

    this.stepCount++;
    this.sampleGroundPlane();
    this.sampleTriangulation();
    // After the depth signals have been updated this step, not before — the
    // whole point is that provisional anchors track the estimate as it improves.
    this.refineProvisionalAnchors();

    const look = this.look();
    const anchors = this.anchorCounts();
    // What an anchor created RIGHT NOW would be placed at, and via which rung.
    // Recorded every frame so a replay can see when rung 2 came online and
    // what it changed placement to — see TelemetrySample.anchorDepthUnits.
    const placement = this.anchorDepth(this.lockedMetersPerUnit());
    this.telemetry.record({
      frameId: this.source.frameId(),
      state: this.state,
      featureCount: this.points.length,
      pos: this.state === 'tracking' ? [this.position.x, this.position.y, this.position.z] : null,
      yaw: look.yaw,
      pitch: look.pitch,
      grabMs,
      trackMs,
      metersPerUnit: this.scale.estimate().metersPerUnit,
      anchorsTotal: anchors.total,
      anchorsCommitted: anchors.committed,
      anchorDepthUnits: placement.depth,
      anchorDepthRung: placement.rung,
      sceneDepthUnits: this.sceneDepth.depth(),
      trackerErrors: this.stepErrors,
    });
  }

  /** Detect a dominant plane; useful for resting fragments on real surfaces. */
  findPlane(): Float32Array | null {
    if (!this.alva) return null;
    try {
      refreshHeapViews(this.alva);
      return this.alva.findPlane(250) ?? null;
    } catch (err) {
      this.noteStepError(err, 'findPlane');
      return null;
    }
  }

  /**
   * "Up" expressed in the SLAM world frame.
   *
   * AlvaAR's world frame is not guaranteed to be gravity-aligned: on the
   * vision-only path it is pinned to the camera at initialisation. So up is
   * carried across from the IMU, which does know where gravity is, rather than
   * assumed.
   *
   * Both quaternions describe the same physical camera at the same instant:
   * `pose.quaternion()` in DevicePose's gravity-aligned frame (Y up), and
   * `this.orientation` in the SLAM frame. Rotating (0,1,0) into camera-local
   * space by one and back out by the other transfers up between them.
   *
   * ⚠️ **Refused (null) on the IMU pose path.** There `this.orientation` IS the
   * IMU quaternion (the vendor copies it through), so this transfer returned
   * exactly (0,1,0) every time — up in the IMU's frame — while `this.position`
   * is in the vision map's frame, which is a different one (the walked motion
   * plane was 40.5° off this "up" on ADVIO-15, 6.3° on the vision path). Every
   * height, floor test and floor raycast built from it measured along the wrong
   * axis, and the verticality test compared an IMU-frame normal... with the
   * same IMU-frame up, so it always passed. The vision map's own rotation is
   * not available on that path, so there is no honest up to give: null, and
   * the floor/metric-scale consumers fall back as they do without a sensor.
   *
   * With maps re-anchored to the device frame (REANCHOR_MAP_TO_DEVICE_ORIENTATION)
   * this is still computed the same way, and comes out near (0,1,0): exactly at
   * the map's first pose, then off by however far the vision rotation and the
   * device's tilt have drifted apart since. The floor test and camera heights
   * are unchanged by the alignment — the plane is rotated by the same A, and a
   * common rotation changes no dot product.
   */
  private worldUp(): Vec3 | null {
    if (!this.pose.hasSensor() || this.state !== 'tracking') return null;
    if (this.useIMU) return null;
    const qd = this.pose.quaternion();
    const localUp = quatRotate({ x: -qd.x, y: -qd.y, z: -qd.z, w: qd.w }, [0, 1, 0]);
    const up = quatRotate(this.orientation, [localUp.x, localUp.y, localUp.z]);
    const len = Math.hypot(up.x, up.y, up.z);
    if (!(len > 1e-6)) return null;
    return { x: up.x / len, y: up.y / len, z: up.z / len };
  }

  /**
   * Sample the floor for metric grounding. Cheap to call — it self-throttles to
   * every PLANE_EVERY steps and does nothing until tracking is stable.
   */
  private sampleGroundPlane(): void {
    if (this.state !== 'tracking') return;
    const locked = this.scale.estimate().state === 'locked';
    // Keep refreshing the plane after the scale locks — anchors are raycast onto
    // it, so it has to stay current if the participant walks into another room.
    // Slower cadence once locked: findPlane runs 250 RANSAC iterations and the
    // floor does not move, and low-tier Android is already latency-bound.
    const every = locked ? PLANE_EVERY * 2 : PLANE_EVERY;
    if (this.stepCount % every !== 0) return;

    const up = this.worldUp();
    if (!up) return;
    // Point and normal in the app frame, or null for a degenerate (NaN) fit.
    // WHICH matrix axis is the normal was measured on real captures, not
    // inferred — see planeFromAlvaMatrix. The old `mat[4..6]` rejected ~97% of
    // floors as walls, which is why metric scale and floor raycasting almost
    // never engaged in the field.
    const raw = planeFromAlvaMatrix(this.findPlane());
    // No information is not disagreement: a degenerate fit neither confirms nor
    // retires the floor we have.
    if (!raw) return;
    // The plane is in the vendor's map frame; the camera pose (and so `up`) is
    // in the map's device-aligned frame — bring the plane across with the same
    // A (identity when not re-anchoring). See REANCHOR_MAP_TO_DEVICE_ORIENTATION.
    const plane = { point: this.toAlignedFrame(raw.point), normal: this.toAlignedFrame(raw.normal) };

    const height =
      (this.position.x - plane.point.x) * up.x +
      (this.position.y - plane.point.y) * up.y +
      (this.position.z - plane.point.z) * up.z;
    const isFloor = isHorizontalPlane(plane.normal, up) && Math.abs(height) > 1e-4;
    if (isFloor) {
      // Raycasting uses the IMU's up rather than the plane's own normal.
      this.groundPlane = { point: plane.point, up, height: Math.abs(height) };
      this.planeRejectRun = 0;
    } else if (++this.planeRejectRun >= PLANE_REJECTS_TO_CLEAR) {
      // One wall, table edge or bad fit used to wipe a good floor instantly.
      // Only a run of disagreeing results retires it.
      this.groundPlane = null;
    }

    this.scale.observe({
      planePoint: plane.point,
      planeNormal: plane.normal,
      cameraPos: this.position,
      worldUp: up,
    });
  }

  /**
   * Intersect a world-space ray with the detected floor.
   *
   * This is what makes a fragment rest on a real surface rather than hang in mid
   * air at a fixed depth. Returns null whenever the answer would be nonsense —
   * no floor yet, looking up, or the hit lands absurdly far away because the ray
   * is nearly parallel to the floor. Callers fall back to fixed-depth placement,
   * which is the pre-existing behaviour.
   *
   * ⚠️ Takes a DIRECTION, not a `Look`. It used to rebuild the ray from
   * `look().yaw/pitch`, which was a frame bug rather than a rounding one — see
   * `anchorDirection()`.
   */
  private raycastFloor(dir: Vec3): Vec3 | null {
    const g = this.groundPlane;
    if (!g || this.state !== 'tracking') return null;

    // Ray/plane: t = ((P - O) . n) / (d . n), with n = up.
    const denom = dir.x * g.up.x + dir.y * g.up.y + dir.z * g.up.z;
    // Looking level or upward relative to the floor — no forward intersection.
    if (denom > -1e-3) return null;

    const num =
      (g.point.x - this.position.x) * g.up.x +
      (g.point.y - this.position.y) * g.up.y +
      (g.point.z - this.position.z) * g.up.z;
    const t = num / denom;
    if (!Number.isFinite(t) || t <= 0) return null;

    // Reject grazing hits: bound the distance relative to the camera's own height
    // above the floor, which is the only length scale we can trust here (it works
    // whatever the map's arbitrary scale happens to be).
    if (t < g.height * 0.4 || t > g.height * MAX_FLOOR_HIT_HEIGHTS) return null;

    return {
      x: this.position.x + dir.x * t,
      y: this.position.y + dir.y * t,
      z: this.position.z + dir.z * t,
    };
  }

  /**
   * Feed the scale-free triangulation consumer from one pass of the feature
   * tracker.
   *
   * The tracker is separate from anything used for pose: it exists to give
   * features stable ids so the same pixel can be found in two frames, which is
   * what makes triangulation possible. It runs at 80x60 with at most 48 points,
   * so it is cheap enough for every tracking step — and it has to run every
   * tracking step, because optical flow needs consecutive frames to keep a
   * point alive. Across any gap in tracking it is reset rather than resumed.
   *
   * ⚠️ Used to also feed `DepthScale` (server-side metric depth via
   * `/api/depth`). Removed 2026-08-26 — it was not the mechanism carrying
   * placement (SceneDepth, rung 2 below, is), it cost a Postgres-adjacent
   * Render plan upgrade to keep the model resident, and it risked an OOM on
   * the first real walking session if that plan were ever downgraded without
   * also touching this code. See docs/HISTORY.md.
   */
  private sampleTriangulation(): void {
    if (this.state !== 'tracking' || !this.canvas) {
      // ⚠️ Across a dropout (or a vetoed pose) the tracker must not carry its
      // ids through: its `prev` frame is from before the gap, so block-matching
      // would pair a pixel from then with one from now under the same id, and
      // SceneDepth's reference pose is from before a possible relocalisation.
      // Re-detect from scratch when tracking resumes. Done once per gap.
      if (!this.triangulationPaused) {
        this.scaleTracker?.reset();
        this.sceneDepth.dropAnchor();
        this.triangulationPaused = true;
      }
      return;
    }
    this.triangulationPaused = false;

    if (!this.scaleTracker) {
      try {
        this.scaleTracker = this.deps.createTracker();
      } catch {
        return; // no 2D context available; scene depth stays unavailable
      }
    }
    // The processing canvas, NOT the live source: it still holds exactly the
    // pixels AlvaAR just produced this pose from. Re-drawing the <video> here,
    // 10-170 ms after the track, sampled a later frame and paired pose N with
    // 2D points from frame N+k. In replay the two are the same frame — the
    // provider does not advance mid-step — so replay results are unchanged.
    const tracked = this.scaleTracker.update(this.canvas);
    if (tracked.points.length === 0) return;

    // The tracker's points are normalised over the WHOLE processing buffer, so
    // they map through the whole frame's geometry at the current calibration —
    // not the renderer's cropped view, and not a module-load constant.
    const view = this.frameView();

    // Scale-free, always on: how far away the room is, in SLAM units. Timed on
    // the frame clock so replay at any speed times out where the live run did.
    this.sceneDepth.offer(
      this.frameNow(),
      this.position,
      this.orientation,
      tracked.points,
      view.tanHalfX,
      view.tanHalfY,
    );
  }

  /**
   * Geometry of the full processing frame at the CURRENT calibrated diagonal.
   * Before the first Initialize(), the source's own size (or the 3:4 default).
   */
  private frameView(): ViewGeometry {
    if (this.procW > 0 && this.procH > 0) return frameGeometry(this.procW, this.procH);
    return viewGeometry(null, this.source?.size() ?? null);
  }

  /** Scene-depth progress, for the HUD. */
  sceneDepthStatus(): SceneDepthStatus {
    return this.sceneDepth.status();
  }

  /**
   * Where to plant a fragment, in SLAM units — the placement ladder.
   *
   * Each rung is preferred over the one below and degrades to it:
   *
   *   1. **metric scale known** — a true `ANCHOR_DEPTH_M` (3.2 m). Also gives correct physical
   *      sizing and floor resting, so it stays the first choice.
   *   2. **room measured** — a fraction of the triangulated scene depth. Assumes
   *      nothing, needs no floor and no server, and is available within a couple
   *      of seconds of walking. This is the rung that actually carries most
   *      sessions, because rung 1 usually never arrives.
   *   3. **nothing known** — the raw constant, which is a lottery.
   *
   * The point of rung 2 is that placement stopped depending on the least
   * reliable quantity in the stack. Scale is now an enhancement rather than a
   * precondition for fragments appearing anywhere sensible.
   */
  private anchorDepthUnits(mpu: number | null): number {
    return this.anchorDepth(mpu).depth;
  }

  /**
   * The placement ladder, reporting WHICH rung it landed on.
   *
   * The rung is what decides whether an anchor is provisional. Rungs 1 and 2
   * are real measurements and commit immediately; rung 3 is the lottery
   * constant and must stay open to correction until something better arrives —
   * however long that takes.
   */
  private anchorDepth(mpu: number | null): { depth: number; rung: 1 | 2 | 3 } {
    if (mpu && mpu > 0) return { depth: ANCHOR_DEPTH_M / mpu, rung: 1 };
    // Callers pass `lockedMetersPerUnit()`, not `metricScale().metersPerUnit`.
    // See that method for why the difference matters.
    const scene = this.sceneDepth.depth();
    if (scene && scene > 0 && Number.isFinite(scene)) {
      return { depth: scene * ANCHOR_DEPTH_SCENE_FRACTION, rung: 2 };
    }
    return { depth: ANCHOR_DEPTH_UNITS, rung: 3 };
  }

  /** Current metric grounding estimate. */
  metricScale(): ScaleEstimate {
    return this.effectiveScale();
  }

  /**
   * Metres per unit, but only once the estimate has actually LOCKED.
   *
   * ⚠️ Placement must use this, never `metricScale().metersPerUnit` directly.
   * `GroundPlaneScale.estimate()` starts reporting a `metersPerUnit` as soon as
   * it has `MIN_SAMPLES` observations, while its state is still `'estimating'`
   * — and it will WITHDRAW that number later, back to null, if the observations
   * turn out to disagree (`spread > MAX_SPREAD` flips the state to
   * `'rejected'`).
   *
   * On the Oppo capture of 2026-08-28 that is exactly what happened:
   * `metersPerUnit` was 0.116 for 150 samples and null for the other 641.
   * `anchorDepth()` consequently swung from 27.44 units to 2.21 units
   * mid-session — a 12× change — so fragments placed either side of the
   * withdrawal sat at wildly different distances in the same room, and none of
   * them were ever re-derived.
   *
   * A provisional scale is worse than no scale, because rung 2 below is a real
   * measurement and rung 1 outranks it.
   */
  private lockedMetersPerUnit(): number | null {
    const est = this.effectiveScale();
    return est.state === 'locked' ? est.metersPerUnit : null;
  }

  /**
   * Scale, from the ground plane.
   *
   * ⚠️ Used to prefer a server-side metric-depth value (`DepthScale`) when
   * locked, since it assumes nothing and needs no floor — unlike the ground
   * plane's 1.5 m stature assumption, which also never actually locks on
   * iPhone (§8). Removed 2026-08-26 along with the rest of the server depth
   * feature (see `sampleTriangulation`'s note); this method is kept as the
   * seam so callers don't need to change, and because rung 2 of
   * `anchorDepth` (SceneDepth) already provides the scale-free placement
   * that mattered in practice, unaffected by this.
   */
  private effectiveScale(): ScaleEstimate {
    return this.scale.estimate();
  }

  /**
   * How trustworthy the world model is right now.
   *
   * The map is "healthy" only once a floor has been found, because the floor is
   * what anchors are raycast onto — without it every fragment falls back to
   * floating at a fixed depth, which is the failure the participant actually
   * sees. Floor detection also happens to be a good proxy for map quality: it
   * needs several agreeing camera-height measurements, which a jumping map
   * cannot produce.
   */
  mapHealth(): MapHealth {
    if (this.state === 'unavailable') {
      return { phase: 'lost', progress: 0, hint: 'tracking unavailable' };
    }
    // Vetoed poses mean the map is contradicting itself right now. Checked
    // BEFORE the not-tracking branch: a veto reports state 'lost', so behind
    // that branch (where it used to be) this hint could never be shown.
    if (this.gate.vetoActive && this.gate.consecutiveRejections > 0) {
      return { phase: 'degraded', progress: 0.5, hint: 'Slow down — move, don’t turn' };
    }
    if (this.state !== 'tracking') {
      return {
        phase: this.gate.everTracked ? 'lost' : 'mapping',
        progress: 0,
        hint: this.gate.everTracked
          ? 'Point at something with more detail'
          : 'Walk slowly sideways to map the space',
      };
    }

    const est = this.scale.estimate();
    if (est.state === 'rejected') {
      return { phase: 'degraded', progress: 0.5, hint: 'Keep the camera level and walk a few steps' };
    }
    if (this.groundPlane && est.state === 'locked') {
      return { phase: 'healthy', progress: 1, hint: null };
    }

    // Still building. Progress is how far along the floor estimate is.
    const progress = Math.max(0.05, Math.min(0.95, est.samples / 24));
    if (this.graceExpired()) {
      // Nothing waits on the floor (anchors never did, see ANCHOR_GRACE_MS),
      // and on most phones it may never come. Report the degraded truth, but
      // stop nagging.
      return { phase: 'degraded', progress, hint: null };
    }
    return {
      phase: 'mapping',
      progress,
      hint: 'Walk slowly sideways to map the space',
    };
  }

  /** Poses vetoed as physically impossible this session. */
  rejectedJumps(): number {
    return this.rejectedInEarlierMaps + this.gate.guard.totalRejected;
  }

  /**
   * Whether to stop asking the participant to walk for a floor. Gates nothing
   * else — anchors are placed and committed by rung, not by clock. See
   * ANCHOR_GRACE_MS.
   */
  private graceExpired(): boolean {
    // Measured from start(), never from firstTrackedAt — a session that never
    // tracks must still reach this deadline. On the frame clock: see startedAt.
    if (this.startedAt === null) return false;
    return this.frameNow() - this.startedAt > ANCHOR_GRACE_MS;
  }

  /**
   * Horizontal FOV of the frame AlvaAR's intrinsics correspond to — i.e. at the
   * diagonal it was initialised with. May lag the calibrated value until a
   * reset. (The `fov` ARGUMENT passed to Initialize differs from this for a
   * landscape buffer; see cameraFov.trackerFovDeg.)
   */
  trackerFovDeg(): number | null {
    if (this.initDiagonal === null || !this.procW || !this.procH) return null;
    return frameHorizontalFovDeg(this.procW, this.procH, this.initDiagonal);
  }

  /** Override the assumed camera height (metres). Invalidates any lock. */
  setAssumedHeight(metres: number): void {
    this.scale.setAssumedHeight(metres);
  }

  resetTracking(): void {
    // AlvaAR.reset() clears the map but KEEPS the intrinsics set at Initialize().
    // So if the FOV has been recalibrated since, a plain reset would silently
    // keep tracking on the old value — drop the instance instead and let
    // ensureAlva() rebuild it with the new one. The generation token makes sure
    // an Initialize() still in flight from before cannot bring the old FOV back.
    const wantedArg = this.procW > 0 ? trackerFovDeg(this.procW, this.procH) : null;
    const fovChanged =
      this.trackerFovArg !== null && wantedArg !== null && Math.abs(this.trackerFovArg - wantedArg) > 0.01;
    // An Initialize() still in flight with a now-outdated diagonal would land
    // AFTER this reset and bring the old FOV back. Discard it (generation bump)
    // and let the next step start a fresh one.
    const pendingStale =
      this.initPending && this.pendingDiagonal !== null && Math.abs(this.pendingDiagonal - cameraDiagonalFovDeg()) > 0.01;
    if (fovChanged || pendingStale) {
      this.releaseInstance();
    } else if (this.alva) {
      try {
        refreshHeapViews(this.alva);
        this.alva.reset();
      } catch (err) {
        // A reset that throws leaves the map in an unknown state: rebuild.
        this.noteStepError(err, 'reset');
        this.releaseInstance();
      }
    }
    this.clearMapState();
    this.continuity.reset();
    this.state = 'reset';
    // Counted HERE: the 'reset' state is overwritten by the next step() before
    // telemetry ever records it, so `resets` was 0 in every capture.
    this.telemetry.markReset();
  }

  look(): Look {
    if (this.state === 'tracking') {
      const f = quatRotate(this.orientation, [0, 0, -1]);
      const u = quatRotate(this.orientation, [0, 1, 0]);
      return {
        yaw: (Math.atan2(f.x, f.z) / DEG + 360) % 360,
        pitch: Math.asin(Math.max(-1, Math.min(1, f.y))) / DEG,
        roll: Math.atan2(u.x, u.y) / DEG,
      };
    }
    return this.pose.look(); // IMU rotation until SLAM locks
  }

  /**
   * The world-space ray a fragment is planted along, with its deterministic
   * spread. Frozen at placement and never recomputed — re-deriving it from a
   * newer pose is how an anchor turns into a billboard.
   *
   * ## Why this is built from the quaternion and not from `look()`
   *
   * The old path went `look()` → yaw/pitch → `{ce·sin az, sin el, ce·cos az}`.
   * While SLAM is tracking that round trip is algebraically an identity, so
   * this is **not** a numerical fix. It closes a FRAME bug:
   *
   * `look()` falls back to `this.pose.look()` — the IMU's gravity-aligned
   * frame — whenever SLAM is not tracking, while `origin` is always
   * `this.position`, which is in the SLAM frame. Those two frames differ by an
   * arbitrary yaw fixed at map init. An anchor placed during a dropout was
   * therefore written as a SLAM-frame origin plus an IMU-frame direction, and
   * pointed somewhere unrelated to where the participant was looking. The Oppo
   * capture of 2026-08-28 spent 29 of 791 samples lost, every one of them
   * eligible to place. `cameraPose()` already documents refusing this exact
   * fallback for the renderer; placement was doing what the renderer was
   * forbidden to.
   *
   * The spread is applied in CAMERA space — yaw about the camera's own up,
   * pitch about its own right — so it stays a rotation of the view direction
   * rather than arithmetic on world Euler angles, and it survives roll (median
   * |roll| was 25° in a real capture) and does not degenerate near the poles.
   */
  private anchorDirection(id: string, orientation: Quat): Vec3 {
    // Two independent hash inputs so yaw and pitch don't move together.
    const yaw = ((hashSeed(id + ':yaw') % 1000) / 1000 - 0.5) * 2 * ANCHOR_SPREAD_YAW_DEG * DEG;
    const pitch =
      ((hashSeed(id + ':pitch') % 1000) / 1000 - 0.5) * 2 * ANCHOR_SPREAD_PITCH_DEG * DEG;
    const cp = Math.cos(pitch);
    // Camera-space forward, tilted by the spread. The camera looks down −z.
    const d = quatRotate(orientation, [
      Math.sin(yaw) * cp,
      Math.sin(pitch),
      -Math.cos(yaw) * cp,
    ]);
    const len = Math.hypot(d.x, d.y, d.z) || 1;
    return { x: d.x / len, y: d.y / len, z: d.z / len };
  }

  /**
   * The pose a NEW anchor is placed from (and the renderer projects with).
   *
   * Once anything has been tracked in this map: the SLAM pose — held through a
   * dropout rather than swapped for the IMU, whose frame differs (see
   * `cameraPose()`). Before the first pose of a map on the IMU path: the IMU
   * orientation at the map origin, because that IS the frame AlvaAR is about
   * to adopt — measured on every capture that starts at init; see the
   * cold-start note on StoredAnchor. Otherwise identity, which on the
   * vision-only path is the camera at map init.
   */
  private placementPose(): { position: Vec3; orientation: Quat } {
    // Only on the IMU POSE path (USE_IMU_ORIENTATION_POSE): that is where
    // AlvaAR's orientation is the IMU's. On the vision path a phone with motion
    // events still gets a map pinned to the camera at init, i.e. identity.
    if (!this.gate.everTracked && this.imuPosePath()) {
      return { position: this.position, orientation: this.pose.quaternion() };
    }
    // Vision path with maps re-anchored to the device frame
    // (REANCHOR_MAP_TO_DEVICE_ORIENTATION): before a map's first pose the frame
    // it is about to be written in IS the device's, so the device orientation
    // is the right one here too — the same reasoning as the IMU path above, and
    // continuous with what the first pose will show. Without a usable sensor
    // reading the map will not be aligned, and identity stays right.
    if (!this.gate.everTracked && this.deviceAlignedMaps()) {
      const q = this.deviceQuatForFrame();
      if (q) return { position: this.position, orientation: q };
    }
    return { position: this.position, orientation: this.orientation };
  }

  anchor(id: string): Anchor {
    if (!this.anchors.has(id)) {
      // Nothing is withheld. The ray is frozen now — origin and world direction
      // are as good as they will ever be — and only the distance along it stays
      // open to correction until the anchor commits. See the StoredAnchor note.
      const from = this.placementPose();
      const dir = this.anchorDirection(id, from.orientation);

      const mpu = this.lockedMetersPerUnit();
      const { depth, rung } = this.anchorDepth(mpu);
      // Jitter is sized from the distance the anchor is FIRST placed at, then
      // frozen as a world offset: sized, so it means the same fraction of the
      // view at any session scale (it was a fixed ±0.8 units — 4 cm one session,
      // a metre the next); frozen, so a later depth correction still slides the
      // text along its line of sight without swinging it sideways. Built from
      // the camera's own right and up axes for the same frame-safety reason as
      // the direction above.
      const jFrac = ((hashSeed(id) % 100) / 100 - 0.5) * 2 * ANCHOR_JITTER_FRACTION;
      const right = quatRotate(from.orientation, [1, 0, 0]);
      const camUp = quatRotate(from.orientation, [0, 1, 0]);
      const jitterAt = (dist: number): Vec3 => {
        const j = jFrac * dist;
        return {
          x: right.x * j + camUp.x * j * 0.4,
          y: right.y * j + camUp.y * j * 0.4,
          z: right.z * j + camUp.z * j * 0.4,
        };
      };

      // A floor hit is already a real surface at a real distance, so it commits
      // immediately — there is nothing better to wait for.
      const hit = this.raycastFloor(dir);
      if (hit) {
        const lift = (mpu && mpu > 0 ? FLOOR_TEXT_LIFT_M / mpu : this.groundPlane!.height * 0.25);
        const up = this.groundPlane!.up;
        const world: Vec3 = {
          x: hit.x + up.x * lift,
          y: hit.y + up.y * lift,
          z: hit.z + up.z * lift,
        };
        const rel = {
          x: world.x - this.position.x,
          y: world.y - this.position.y,
          z: world.z - this.position.z,
        };
        const d = Math.hypot(rel.x, rel.y, rel.z) || 1;
        // Keep the jitter IN the floor plane: remove its component along the
        // floor's up. This used to zero world y, but the SLAM frame's y is not
        // guaranteed to be gravity-up — `up` here is, carried from the IMU.
        const o = jitterAt(d);
        const along = o.x * up.x + o.y * up.y + o.z * up.z;
        this.anchors.set(id, {
          origin: { ...this.position },
          dir: { x: rel.x / d, y: rel.y / d, z: rel.z / d },
          depth: d,
          offset: { x: o.x - up.x * along, y: o.y - up.y * along, z: o.z - up.z * along },
          provisional: false,
          // A real surface at a real distance — never revised, not even by a
          // later metric lock, because the floor already IS the ground truth.
          rung: 0,
          normal: { ...up },
        });
      } else {
        this.anchors.set(id, {
          origin: { ...from.position },
          dir,
          depth,
          offset: jitterAt(depth),
          // Provisional means PLACED ON A GUESS, not "the app is young".
          //
          // This used to be `mapHealth() !== healthy && !graceExpired()`, a
          // global 9 s timer from backend start — which made the whole
          // refinement mechanism inert. Fragments surface over the course of a
          // minute, so by the time most of them anchored the global deadline had
          // long passed and every one committed instantly to whatever depth
          // existed. The capture of 2026-08-25 proves it: `committed === total`
          // in all 817 samples, i.e. nothing was ever provisional, across a
          // session where the depth was demonstrably wrong.
          //
          // Rungs 1 and 2 are real measurements — commit. Rung 3 is the lottery
          // constant, so it stays open until a real measurement arrives, however
          // late that is. An anchor's own placement quality is the only thing
          // that should decide this.
          provisional: rung === 3,
          rung,
        });
      }
    }
    return this.resolve(id)!;
  }

  /**
   * Refine the depth of every still-provisional anchor, and commit those whose
   * window has closed.
   *
   * Runs from `step()`, so correction tracks the scene estimate as it improves
   * rather than snapping once at the deadline. Only the distance along each
   * frozen ray changes, so text slides toward or away from the participant and
   * never sideways.
   */
  private refineProvisionalAnchors(): void {
    if (this.anchors.size === 0) return;
    const mpu = this.lockedMetersPerUnit();
    const { depth, rung } = this.anchorDepth(mpu);
    // Nothing better than the lottery constant is available yet, so there is
    // nothing to correct toward. Leave every provisional anchor exactly where
    // it is and wait — committing here would freeze the guess.
    if (rung === 3) return;

    for (const a of this.anchors.values()) {
      if (a.provisional) {
        // A real measurement arrived. Slide along the frozen ray to the true
        // distance and stop revisiting it.
        //
        // Committing here is only safe because rung 2 now means the scene
        // estimate has AGREED with itself, not merely produced a number. It
        // used to fire on the first two triangulations of the session, so a
        // whole burst of fragments — the corpus seeds all anchor within one
        // frame of entering the field — inherited a single arbitrary sample
        // and froze onto it. See SceneDepth's MIN_ESTIMATES.
        a.depth = depth;
        a.rung = rung;
        a.provisional = false;
        continue;
      }
      // Committed, but on a lower rung than is now available. Metric scale is
      // strictly better than scene depth — it is the same physical distance
      // every session — so let it through once. Nothing moves sideways; this
      // slides along the same frozen ray as any other depth correction.
      if (rung === 1 && a.rung === 2) {
        a.depth = depth;
        a.rung = 1;
      }
    }
  }

  /**
   * Anchor along the ray through a normalised image point — the Watcher naming
   * something in the frame and the fragment landing on it.
   *
   * The ray is built from the same view geometry the renderer projects with, so
   * an anchor placed at image point p projects back to p. Depth comes from the
   * scene estimate rather than a constant: the whole value of this path is that
   * the fragment sits at the distance of the actual object, and a fixed depth
   * would put it in front of or behind the thing being referred to.
   *
   * ## Pose-stamping, and why this takes a pose argument
   *
   * ⚠️ **`at` must be the pose the FRAME WAS CAPTURED AT, not the current one.**
   *
   * A vision reply arrives seconds after its frame was taken — inference plus a
   * round trip, on a ~22 s cadence. By then the participant has turned. Building
   * the ray from the live pose fires it from wherever they are pointing NOW,
   * through an image coordinate describing where they were pointing THEN, and it
   * lands on nothing in particular. That single mismatch is the reason object
   * grounding read as arbitrary rather than merely imprecise.
   *
   * AlvaAR world coordinates are stable across the interval, so a ray cast from
   * a five-second-old pose still lands in the correct world position. Stamping
   * the pose at capture removes latency from placement completely — which is
   * what lets the delay become the artwork's own device (the line is spoken now,
   * the writing is already on the object when you turn back) instead of a defect
   * to be minimised.
   *
   * Falls back to the live pose only when no stamp is supplied.
   *
   * ## Never declines
   *
   * Placement is best-effort by instruction: an unknown scene depth falls back
   * to the standard anchor depth rather than returning null. A fragment at
   * roughly the right bearing beats no fragment — and the bearing, which is the
   * part the Watcher actually chose, is always honoured.
   */
  anchorAtImagePoint(id: string, nx: number, ny: number, at?: CameraPose): Anchor | null {
    if (this.anchors.has(id)) return this.resolve(id);

    // A pose from before a reset is in a map that no longer exists — different
    // origin, orientation and scale. Mixing it with this one is the bug, so it
    // is refused rather than "best-efforted" (the one case this method declines).
    if (at && at.epoch !== undefined && at.epoch !== this.epoch) return null;
    const pose = at ?? this.placementPose();
    const p = pose.position;
    const q = pose.orientation;
    if (![p.x, p.y, p.z, q.x, q.y, q.z, q.w, nx, ny].every(Number.isFinite)) return null;

    // The geometry of the WHOLE captured frame at the current calibration: the
    // Watcher's box is normalised against the full frame it was sent (see
    // sensing/captureFrame.ts), not against the cropped view the renderer
    // shows. The renderer's own geometry then handles the `object-fit` crop
    // when projecting the result back, so image point p still lands on p.
    // (This used to call `viewGeometry(null)`, a module-load constant that
    // ignored both calibration and aspect.)
    const view = this.frameView();

    // Normalised image point -> camera-space direction. y is flipped because
    // image y grows downward and camera y grows up.
    const sx = (nx * 2 - 1) * view.tanHalfX;
    const sy = (1 - ny * 2) * view.tanHalfY;
    const dirCam: Vec3 = { x: sx, y: sy, z: -1 };
    const len = Math.hypot(dirCam.x, dirCam.y, dirCam.z) || 1;
    // Rotated by the CAPTURE orientation — see the pose-stamping note above.
    const dir = quatRotate(pose.orientation, [
      dirCam.x / len,
      dirCam.y / len,
      dirCam.z / len,
    ]);

    // Depth ALONG THIS RAY, not the room's average.
    //
    // This is the one place where a per-bearing depth is unambiguously right:
    // the direction was chosen by the Watcher looking at a specific object, and
    // the participant was pointing at it when the frame was taken — so it is a
    // direction the camera has certainly observed, which is exactly where the
    // cloud has points. Using the session median instead put the writing at the
    // room's average distance along the right bearing, i.e. floating in front of
    // or behind the thing it names.
    //
    // Ladder, most specific first: the stuff actually along this ray, then the
    // room's median, then the ordinary placement depth.
    const rayDepth = this.sceneDepth.depthAlongRay(pose.position, dir);
    const scene = this.sceneDepth.depth();
    const depth =
      rayDepth !== null && rayDepth > 0
        ? rayDepth
        : scene !== null && scene > 0 && Number.isFinite(scene)
          ? scene
          : this.anchorDepthUnits(this.lockedMetersPerUnit());

    this.anchors.set(id, {
      origin: { ...pose.position },
      dir,
      depth,
      offset: { x: 0, y: 0, z: 0 },
      // The Watcher chose this spot by looking at it. Sliding it later would
      // move the writing off the thing it names, so it commits on placement —
      // and rung 0 keeps the upgrade path off it too, for the same reason.
      provisional: false,
      rung: 0,
    });
    return this.resolve(id);
  }

  resolve(id: string): Anchor | null {
    const a = this.anchors.get(id);
    if (!a) return null;
    // The stored ray, evaluated. Origin and direction are frozen, so this is a
    // fixed world point for any given depth — NOT a bearing recomputed from the
    // live pose, which is what mirrored every anchor for the life of the
    // project. The renderer projects the result with the full camera pose. See
    // field/placement.ts.
    const world: Vec3 = {
      x: a.origin.x + a.dir.x * a.depth + a.offset.x,
      y: a.origin.y + a.dir.y * a.depth + a.offset.y,
      z: a.origin.z + a.dir.z * a.depth + a.offset.z,
    };
    // Provisional anchors report reduced — but non-zero — confidence. Zero
    // means "do not draw this", and the whole point of the provisional state is
    // that the fragment IS drawn while its distance settles.
    const base = this.state === 'tracking' ? 1 : 0.3;
    return {
      id,
      world,
      confidence: a.provisional ? Math.min(base, 0.55) : base,
      normal: a.normal ? { ...a.normal } : undefined,
      // How far away this was MEANT to be. Renderers size their distance fades
      // against it, because a SLAM unit is arbitrary per session — see Anchor.
      placementDistance: a.depth,
    };
  }

  /** How many anchors are placed, and how many have committed. Diagnostic. */
  anchorCounts(): { total: number; committed: number } {
    let committed = 0;
    for (const a of this.anchors.values()) if (!a.provisional) committed += 1;
    return { total: this.anchors.size, committed };
  }

  /**
   * The pose the renderer projects with.
   *
   * Deliberately does NOT fall back to the IMU when tracking is lost, though
   * `look()` does. Anchors are written in the SLAM world frame, and the device
   * frame is a different one — swinging the projection with the IMU would drag
   * every fragment across the room the moment tracking dropped. Holding the last
   * SLAM pose freezes them instead, which is both honest and far less alarming.
   */
  cameraPose(): CameraPose {
    // Before the first pose of a map on the IMU path, the IMU orientation — the
    // frame AlvaAR is about to adopt, and the one cold-start anchors were
    // placed with (placementPose). Once tracked, never the IMU (see above).
    const p = this.placementPose();
    return { position: { ...p.position }, orientation: { ...p.orientation }, epoch: this.epoch };
  }

  confidence(): number {
    return this.state === 'tracking' ? 1 : 0.3;
  }

  featurePoints(): { x: number; y: number }[] {
    if (!this.procW || !this.procH) return [];
    return this.points.map((p) => ({ x: p.x / this.procW, y: p.y / this.procH }));
  }

  trackingState(): TrackingState {
    return this.state;
  }

  usingIMU(): boolean {
    return this.useIMU;
  }
}

function hashSeed(id: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
